import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled, { ThemeProvider } from 'styled-components';
import { Backdrop, Head, Loader, Nav, Social, Email, Footer } from '@components';
import { GlobalStyle, theme } from '@styles';
import { Analytics } from '@vercel/analytics/react';

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

// True once any page in this tab has mounted. Module scope, not `window`: it
// survives client-side route changes but resets on a real page load, which is
// exactly when the intro should play again. Reading it during render is safe
// because it is `false` on the server too, so the first client render matches
// the SSR output.
//
// Keyed on "has anything rendered yet" rather than "has the intro finished":
// the intro belongs to a genuine first landing on the home page. Land on a
// blog post and the intro never runs, so a flag set by the loader's own
// completion would still be false when you clicked through to the home page —
// which is how the hexagon animation turned up on a navigation.
let hasRenderedOnce = false;

const Layout = ({ children, location }) => {
  const isHome = location.pathname === '/';
  const [isLoading, setIsLoading] = useState(isHome && !hasRenderedOnce);

  useEffect(() => {
    hasRenderedOnce = true;
  }, []);

  // Sets target="_blank" rel="noopener noreferrer" on external links
  const handleExternalLinks = () => {
    const allLinks = Array.from(document.querySelectorAll('a'));
    if (allLinks.length > 0) {
      allLinks.forEach(link => {
        if (link.host !== window.location.host) {
          link.setAttribute('rel', 'noopener noreferrer');
          link.setAttribute('target', '_blank');
        }
      });
    }
  };

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (location.hash) {
      const id = location.hash.substring(1); // location.hash without the '#'
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView();
          el.focus();
        }
      }, 0);
    }

    handleExternalLinks();
    // Covers a deep link that arrives while the intro is still playing;
    // ordinary anchor clicks are handled by onRouteUpdate in gatsby-browser.js,
    // because a hash-only change never re-renders the page component.
  }, [isLoading, location.hash]);

  return (
    <>
      <Analytics />
      <Head />

      <div id="root">
        <ThemeProvider theme={theme}>
          <GlobalStyle />

          <Backdrop />

          {/* The loader is an overlay, not a replacement. Rendering the real
              content underneath keeps it in the SSR'd HTML, which is what lets
              Gatsby restore scroll position on back-navigation. */}
          {isLoading && isHome && (
            <Loader finishLoading={() => setIsLoading(false)} />
          )}

          <StyledContent>
            <Nav isHome={isHome} />
            <Social isHome={isHome} />
            <Email isHome={isHome} />

            {/* A class, not an id: as #content it was an anchor target, so
                /#content was a real URL that scrolled here. Only the blur
                overlay needs to address this element. */}
            <div className="page-content">
              {children}
              <Footer isHome={isHome} />
            </div>
          </StyledContent>
        </ThemeProvider>
      </div>
    </>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired,
};

export default Layout;
